import type { ShadowMessage } from "./types";

type Body = Record<string, unknown> | undefined;

const MESSAGE_SUGGESTION_ERROR: string = "message-suggestion-error";
const MESSAGE_SUGGESTION_EVENTS: ReadonlySet<string> = new Set(["message-suggestion", MESSAGE_SUGGESTION_ERROR]);

/** Identifiers each backend mints independently for the same logical proactive message. */
const MESSAGE_SUGGESTION_IGNORED_KEYS: readonly string[] = ["correlationId", "messageId"];

/** Subscribe/unsubscribe acks: GAIA echoes `sessionId` + `sessionGroupId`, Simplr only `sessionId`. */
const SUBSCRIPTION_ACK_EVENTS: ReadonlySet<string> = new Set([
    "session-subscription-success",
    "session-unsubscription-success",
]);

/**
 * Events whose bodies differ by design, so a matched pair is reported `equal` without diffing.
 * Pairing still measures that both backends answered, and how far apart.
 *
 * - `update-session-context-success`: GAIA echoes the orchestrator's session-context response,
 *   Simplr only the flat context snapshot (see `buildMatchKey`), so every pair would be `different`.
 */
const PRESENCE_ONLY_EVENTS: ReadonlySet<string> = new Set(["update-session-context-success"]);

const NO_KEYS: readonly string[] = Object.freeze([]);

const asBody = (message: ShadowMessage): Body => {
    const body = message.body as unknown;
    return typeof body === "object" && body !== null ? (body as Record<string, unknown>) : undefined;
};

const asString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

/** `sessionGroupId ?? sessionId` from the body: the workspace session both backends are asked about. */
export function extractSessionId(message: ShadowMessage): string | undefined {
    const body = asBody(message);
    if (!body) return undefined;
    return asString(body.sessionGroupId ?? body.sessionId);
}

/** Nested under `body.suggestion` on both backends; the flat `body.messageType` fallback is defensive only. */
function extractMessageType(message: ShadowMessage): string {
    const body = asBody(message);
    const suggestion = body?.suggestion;
    const nested =
        typeof suggestion === "object" && suggestion !== null
            ? asString((suggestion as Record<string, unknown>).messageType)
            : undefined;
    return (nested ?? asString(body?.messageType) ?? "").toLowerCase();
}

/**
 * Returns the string that two messages (one per backend) must share to be treated as the
 * same logical response. The default is `name | correlationId | sessionId`; a few events
 * need a different identity because the two backends do not echo the same fields:
 *
 * - `message-suggestion[-error]`: both backends set the envelope correlationId to the
 *   suggestion's own `messageId`, which each backend mints independently (GAIA often
 *   leaves it empty), so it can never match. Pair by session and `messageType` instead
 *   (GAIA: "Proactive"/"search" in send_sns_notification.py; Simplr: "Proactive" default
 *   in suggestionMapper.ts). Same-key messages are then paired in arrival order. Errors
 *   key by session only: Simplr's mapServerErrorToSuggestion always sends `messageType: null`
 *   while GAIA forwards the producer's, so the type would never match.
 * - `update-session-context-success`: GAIA echoes the orchestrator's session-context
 *   response (carries session ids) while Simplr echoes only the flat context snapshot
 *   (updateSessionContext.ts), so the Simplr body has no session. The request
 *   correlationId is the workspace session id, so it is the session discriminator.
 * - `session-subscription-success` / `session-unsubscription-success`: the request carries
 *   both `sessionGroupId` (workspace session) and `sessionId` (call/chat id). Both backends
 *   echo `sessionId`, but only GAIA also echoes `sessionGroupId`, so the default
 *   `sessionGroupId ?? sessionId` lands on different values. Key by `sessionId` first.
 */
export function buildMatchKey(message: ShadowMessage): string {
    const correlationId = message.correlationId ?? "";

    if (MESSAGE_SUGGESTION_EVENTS.has(message.name)) {
        const messageType = message.name === MESSAGE_SUGGESTION_ERROR ? "" : extractMessageType(message);
        return `${message.name}|${extractSessionId(message) ?? ""}|${messageType}`;
    }

    if (message.name === "update-session-context-success") {
        return `${message.name}|${correlationId}`;
    }

    if (SUBSCRIPTION_ACK_EVENTS.has(message.name)) {
        const sessionId = asString(asBody(message)?.sessionId) ?? extractSessionId(message) ?? "";
        return `${message.name}|${correlationId}|${sessionId}`;
    }

    return `${message.name}|${correlationId}|${extractSessionId(message) ?? ""}`;
}

/**
 * Per-event keys to skip when diffing, on top of the comparator's global ignored keys.
 * Limited to identifiers that are unique per backend by construction, so a difference in
 * them says nothing about content.
 */
export function ignoredKeysFor(eventName: string): readonly string[] {
    return MESSAGE_SUGGESTION_EVENTS.has(eventName) ? MESSAGE_SUGGESTION_IGNORED_KEYS : NO_KEYS;
}

/** True when matched pairs of this event are counted as `equal` without diffing their bodies. */
export function isPresenceOnly(eventName: string): boolean {
    return PRESENCE_ONLY_EVENTS.has(eventName);
}
